import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export type SpendBreakdownScope =
  | { kind: 'experiences'; values: string[]; label?: string }
  | { kind: 'teams'; values: string[]; label?: string }
  | { kind: 'catch-all'; values: string[]; label?: string };

export interface SpendTrends {
  ytd: number;
  ytdTarget: number;
  prevMonthComplete: number;
  prevMonthSamePeriod: number;
  currMonthToDate: number;
  currMonthRunRate: number;
  projectedAnnual: number;
  projectedAnnualTarget: number;
  projectedGap: number;
  momChangeRatio: number | null;
}

export interface Periods {
  asOf: string;
  monthsElapsed: number;
  monthsRemaining: number;
  dayOfMonth: number;
  daysInCurrMonth: number;
  ytdFrom: string;
  currMonthFrom: string;
  prevMonthFrom: string;
}

export interface TeamListRow {
  team: string;
  headName: string | null;
  headSlug: string | null;
  vpName: string | null;
  vpSlug: string | null;
  spend: number;
  savings: number;
}

export interface TeamListResponse {
  rows: TeamListRow[];
  from: string;
  to: string;
  windowDays: number;
}

export interface SpendBreakdown {
  name: string;
  slug: string;
  actual: number;
  savings: number;
  shouldBe: number;
  scopes?: SpendBreakdownScope[];
  trends?: SpendTrends;
  unavailable?: boolean;
}

export interface BreakdownResponse {
  rows: SpendBreakdown[];
  total: SpendBreakdown;
  from: string;
  to: string;
  windowDays: number;
  vpName?: string;
  periods?: Periods;
}

export interface UnattributedRow {
  team: string;
  spend: number;
  isUntagged: boolean;
}

export interface UnattributedResponse {
  vpName: string;
  rows: UnattributedRow[];
  total: number;
  from: string;
  to: string;
  windowDays: number;
  trends?: SpendTrends;
  periods?: Periods;
}

export interface TeamSpendRow {
  team: string;
  spend: number;
}

export interface TeamCostsResponse {
  vpName: string;
  headName: string;
  rows: TeamSpendRow[];
  total: number;
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export interface ProductSpendRow {
  product: string;
  spend: number;
}

export interface TeamProductsResponse {
  team: string;
  rows: ProductSpendRow[];
  total: number;
  from: string;
  to: string;
  windowDays: number;
  trends?: SpendTrends;
  periods?: Periods;
}

export interface UsageTypeSpendRow {
  usageType: string;
  spend: number;
}

export interface TeamProductUsageResponse {
  team: string;
  product: string;
  rows: UsageTypeSpendRow[];
  total: number;
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export type Confidence = 'tag' | 'mapped' | 'derived' | 'unattributed';
export type Product = 'eks' | 'rds' | 'elasticache' | 'cloudfront' | 'other';
export type Env =
  | 'prod-eu'
  | 'prod-us'
  | 'prod-au'
  | 'staging'
  | 'management'
  | 'monitoring'
  | 'playground';

export interface ServiceRow {
  service: string | null;
  totalCost: number;
  perEnv: Partial<Record<Env | 'unknown', number>>;
  resourceCount: number;
  products: Product[];
  confidence: Confidence;
  derivedShare: number;
  allocated?: number;
  trends?: SpendTrends;
}

export interface TeamServicesResponse {
  team: string;
  rows: ServiceRow[];
  total: ServiceRow;
  coverage: Record<Confidence, number>;
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export interface ResourceRow {
  resourceId: string;
  product: Product;
  env: Env | 'unknown';
  cost: number;
  confidence: Confidence;
  team?: string;
  isAllocated?: boolean;
}

export interface ServiceResourcesResponse {
  team: string;
  service: string;
  rows: ResourceRow[];
  total: ResourceRow;
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export interface MonthlySpend {
  month: string;
  label: string;
  cost: number;
  partial: boolean;
}

export interface MonthlyHistoryResponse {
  scope: string;
  rows: MonthlySpend[];
  months: number;
  to: string;
}

export type MonthlyItemSource = 'aws' | 'gcp' | 'saas' | 'other';

export interface MonthlyItem {
  source: MonthlyItemSource;
  service: string;
  category: string;
  cost: number;
}

export interface MonthlyItemsResponse {
  month: string;
  label: string;
  projected: boolean;
  rows: MonthlyItem[];
  total: number;
  from: string;
  to: string;
}

export type OverviewSourceKind =
  | 'aws-flagship'
  | 'aws-other'
  | 'aws-overhead'
  | 'gcp-services'
  | 'gcp-marketplace'
  | 'saas';

export interface OverviewSource {
  id: string;
  label: string;
  kind: OverviewSourceKind;
  cost: number;
  ownerVpSlug: string | null;
  ownerVpName: string | null;
  ownerTeam: string | null;
  granular: boolean;
}

export interface OverviewOwnerRow {
  vpSlug: string;
  vpName: string;
  cost: number;
}

export interface TotalOverviewResponse {
  total: number;
  unowned: number;
  sources: OverviewSource[];
  byOwner: OverviewOwnerRow[];
  flagship: { whole: number; operating: number; overhead: number };
  from: string;
  to: string;
  windowDays: number;
  savings?: number;
  shouldBe?: number;
  trends?: SpendTrends;
  periods?: Periods;
}

export interface BudgetMonth {
  month: string;
  label: string;
  cost: number;
  partial: boolean;
  projected: boolean;
}

export interface BudgetOutlookResponse {
  year: number;
  ytdActual: number;
  mtdActual: number;
  monthsElapsed: number;
  monthsRemaining: number;
  recentRunRate: number;
  annualGrowthPct: number;
  yoyComparable: boolean;
  projectedMonth: number;
  thisYearProjected: number;
  thisYearFlatProjection: number;
  annualBudget: number | null;
  budgetVariance: number | null;
  overBudget: boolean | null;
  nextYearBudget: number;
  monthly: BudgetMonth[];
  series?: BudgetMonth[];
}

export interface TaxonomyVp {
  name: string;
  slug: string;
  label: string;
  experiences: string[];
  isCatchAll: boolean;
}

export interface TaxonomyHead {
  vpSlug: string;
  name: string;
  slug: string;
  experience: string;
  teams: string[];
}

export interface TaxonomyResponse {
  experiences: string[];
  vps: TaxonomyVp[];
  heads: TaxonomyHead[];
  teams: string[];
}

export interface EnvServiceRow {
  service: string;
  cost: number;
  compute: number;
  database: number;
  allocated?: number;
}

export interface EnvGroup {
  env: Env | 'unknown';
  total: number;
  services: EnvServiceRow[];
  unattributed: number;
  compute: number;
  database: number;
}

export interface TeamEnvServicesResponse {
  team: string;
  envs: EnvGroup[];
  total: number;
  savings?: number;
  shouldBe?: number;
  from: string;
  to: string;
  windowDays: number;
  trends?: SpendTrends;
  periods?: Periods;
}

export interface FleetServicesResponse {
  rows: ServiceRow[];
  total: ServiceRow;
  coverage: Record<string, number>;
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export type SaasSource = 'saas' | 'marketplace';

export interface SaasProviderSpend {
  provider: string;
  cost: number;
  source: SaasSource;
}

export interface SaasCostsResponse {
  rows: SaasProviderSpend[];
  total: number;
  from: string;
  to: string;
  windowDays: number;
  trends?: SpendTrends;
  periods?: Periods;
}

export interface CalloutsResponse {
  configured: boolean;
  notebookId?: string;
  name?: string;
  markdown?: string;
  url?: string;
}

export interface EnvUntaggedRow {
  product: string;
  cost: number;
}

export interface EnvUntaggedResponse {
  team: string;
  env: string;
  rows: EnvUntaggedRow[];
  total: number;
  from: string;
  to: string;
  windowDays: number;
}

export interface OptimizationRecord {
  id: string;
  source: 'datadog';
  team: string;
  experience: string;
  recommendationType: string;
  resourceId: string;
  resourceType: string;
  datadogResourceKey: string | null;
  awsAccountName: string | null;
  awsAccountId: string | null;
  awsRegion: string | null;
  eksClusterName: string | null;
  annualSavings: number;
  currency: string;
}

export interface OptimizationSummary {
  key: string;
  name: string;
  count: number;
  annualSavings: number;
}

export interface OptimizationHeadSummary extends OptimizationSummary {
  slug: string;
  teams: OptimizationSummary[];
}

export interface OptimizationVpSummary extends OptimizationSummary {
  slug: string;
  heads: OptimizationHeadSummary[];
}

export interface OptimizationHierarchy {
  total: OptimizationSummary;
  unattributed: OptimizationSummary;
  vps: OptimizationVpSummary[];
}

export interface OptimizationsResponse {
  rows: OptimizationRecord[];
  hierarchy: OptimizationHierarchy;
  coverage: OptimizationCoverage;
  datadogAppBaseUrl?: string;
}

export interface OptimizationCoverage {
  totalRecords: number;
  teamTaggedRecords: number;
  attributedAnnualSavings: number;
  unattributedAnnualSavings: number;
}

export interface EnvironmentSpendRow {
  environment: string;
  spend: number;
  isUntagged?: boolean;
}

export interface TeamEnvironmentsResponse {
  team: string;
  rows: EnvironmentSpendRow[];
  total: { environment: string; spend: number; trends?: SpendTrends };
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export interface DeploymentSpendRow {
  deployment: string;
  spend: number;
  isUntagged?: boolean;
}

export interface TeamEnvironmentDeploymentsResponse {
  team: string;
  environment: string;
  rows: DeploymentSpendRow[];
  total: { deployment: string; spend: number };
  from: string;
  to: string;
  windowDays: number;
  periods?: Periods;
}

export type CostByTagGroup = 'experience' | 'team';
export type CostByTagPeriod = 'month' | 'ytd';

export interface CostByTagRow {
  key: string;
  cost: number;
}

export interface CostByTagResponse {
  group: CostByTagGroup;
  period: CostByTagPeriod;
  from: string;
  to: string;
  rows: CostByTagRow[];
  total: number;
}

export interface Ec2CommitmentSlices {
  sp3yr: number;
  sp1yr: number;
  onDemand: number;
  spot: number;
  other: number;
}

export interface Ec2CommitmentPlan {
  arn: string;
  term: '1yr' | '3yr';
  endTime: string | null;
  cost: number;
  /** Account owning the plan; `null` when the ARN can't be parsed. */
  accountId: string | null;
  /** Known payer name; `null` for linked member accounts — render the id. */
  accountLabel: string | null;
}

export interface Ec2CommitmentLicenceBearing {
  covered: number;
  uncovered: number;
}

export interface Ec2CommitmentResponse {
  windowDays: number;
  from: string;
  to: string;
  total: number;
  slices: Ec2CommitmentSlices;
  plans: Ec2CommitmentPlan[];
  /**
   * Windows/SQL/paid-distro instance spend (spot excluded). Covered by SPs
   * like any other instance usage, but SPs discount only the compute portion
   * of these rates — disclosed so the uncovered pool isn't read as uniformly
   * commitment-worthy.
   */
  licenceBearing: Ec2CommitmentLicenceBearing;
}

export interface FinopsApi {
  getOrgBreakdown(windowDays: number): Promise<BreakdownResponse>;
  getVpBreakdown(slug: string, windowDays: number): Promise<BreakdownResponse>;
  getUnattributedBreakdown(
    slug: string,
    windowDays: number,
  ): Promise<UnattributedResponse>;
  getHeadTeamCosts(
    vpSlug: string,
    headSlug: string,
    windowDays: number,
  ): Promise<TeamCostsResponse>;
  getTeamProductBreakdown(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamProductsResponse>;
  getTeamProductUsageBreakdown(
    teamTag: string,
    productTag: string,
    windowDays: number,
  ): Promise<TeamProductUsageResponse>;
  getTeamEnvironments(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamEnvironmentsResponse>;
  getTeamEnvironmentDeployments(
    teamTag: string,
    env: string,
    windowDays: number,
  ): Promise<TeamEnvironmentDeploymentsResponse>;
  getTeamServices(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamServicesResponse>;
  getServiceResources(
    teamTag: string,
    service: string,
    windowDays: number,
  ): Promise<ServiceResourcesResponse>;
  getTeamEnvServices(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamEnvServicesResponse>;
  getEnvUntagged(
    teamTag: string,
    env: string,
    windowDays: number,
  ): Promise<EnvUntaggedResponse>;
  getFleetServices(windowDays: number): Promise<FleetServicesResponse>;
  getFleetServiceResources(
    service: string,
    windowDays: number,
  ): Promise<ServiceResourcesResponse>;
  getMonthlyHistory(opts?: {
    vp?: string;
    head?: string;
    team?: string;
    months?: number;
  }): Promise<MonthlyHistoryResponse>;
  getMonthlyItems(month: string): Promise<MonthlyItemsResponse>;
  getTotalOverview(windowDays: number): Promise<TotalOverviewResponse>;
  getBudgetOutlook(opts?: {
    vp?: string;
    head?: string;
    team?: string;
    year?: number;
  }): Promise<BudgetOutlookResponse>;
  getSaasCosts(windowDays: number): Promise<SaasCostsResponse>;
  getCallouts(): Promise<CalloutsResponse>;
  getTaxonomy(): Promise<TaxonomyResponse>;
  getTeams(windowDays?: number): Promise<TeamListResponse>;
  getOptimizations(team?: string): Promise<OptimizationsResponse>;
  getCostByTag(opts: {
    group: CostByTagGroup;
    period: CostByTagPeriod;
  }): Promise<CostByTagResponse>;
  getEc2Commitment(windowDays?: number): Promise<Ec2CommitmentResponse>;
}

export const finopsApiRef = createApiRef<FinopsApi>({
  id: 'plugin.finops',
});

export class FinopsApiClient implements FinopsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('finops');
  }

  async getOrgBreakdown(windowDays: number): Promise<BreakdownResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/org?window=${windowDays}`,
    );
    return parseResponse(response, 'org breakdown');
  }

  async getVpBreakdown(
    slug: string,
    windowDays: number,
  ): Promise<BreakdownResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/vp/${encodeURIComponent(slug)}?window=${windowDays}`,
    );
    return parseResponse(response, `VP "${slug}" breakdown`);
  }

  async getUnattributedBreakdown(
    slug: string,
    windowDays: number,
  ): Promise<UnattributedResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/vp/${encodeURIComponent(slug)}/unattributed?window=${windowDays}`,
    );
    return parseResponse(response, `VP "${slug}" unattributed breakdown`);
  }

  async getHeadTeamCosts(
    vpSlug: string,
    headSlug: string,
    windowDays: number,
  ): Promise<TeamCostsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/vp/${encodeURIComponent(vpSlug)}/head/${encodeURIComponent(headSlug)}/teams?window=${windowDays}`,
    );
    return parseResponse(
      response,
      `head "${headSlug}" team costs under VP "${vpSlug}"`,
    );
  }

  async getTeamProductBreakdown(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamProductsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/products?window=${windowDays}`,
    );
    return parseResponse(response, `team "${teamTag}" AWS product breakdown`);
  }

  async getTeamProductUsageBreakdown(
    teamTag: string,
    productTag: string,
    windowDays: number,
  ): Promise<TeamProductUsageResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/product/${encodeURIComponent(productTag)}/usage?window=${windowDays}`,
    );
    return parseResponse(
      response,
      `team "${teamTag}" / product "${productTag}" usage-type breakdown`,
    );
  }

  async getTeamEnvironments(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamEnvironmentsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/environments?window=${windowDays}`,
    );
    return parseResponse(response, `team "${teamTag}" environment breakdown`);
  }

  async getTeamEnvironmentDeployments(
    teamTag: string,
    env: string,
    windowDays: number,
  ): Promise<TeamEnvironmentDeploymentsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/environment/${encodeURIComponent(env)}/deployments?window=${windowDays}`,
    );
    return parseResponse(
      response,
      `team "${teamTag}" / environment "${env}" deployment breakdown`,
    );
  }

  async getTeamServices(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamServicesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/services?window=${windowDays}`,
    );
    return parseResponse(response, `team "${teamTag}" service breakdown`);
  }

  async getServiceResources(
    teamTag: string,
    service: string,
    windowDays: number,
  ): Promise<ServiceResourcesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/service/${encodeURIComponent(service)}/resources?window=${windowDays}`,
    );
    return parseResponse(
      response,
      `team "${teamTag}" / service "${service}" resources`,
    );
  }

  async getTeamEnvServices(
    teamTag: string,
    windowDays: number,
  ): Promise<TeamEnvServicesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/env-services?window=${windowDays}`,
    );
    return parseResponse(response, `team "${teamTag}" env-services breakdown`);
  }

  async getEnvUntagged(
    teamTag: string,
    env: string,
    windowDays: number,
  ): Promise<EnvUntaggedResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/team/${encodeURIComponent(teamTag)}/environment/${encodeURIComponent(env)}/untagged?window=${windowDays}`,
    );
    return parseResponse(
      response,
      `team "${teamTag}" / env "${env}" untagged breakdown`,
    );
  }

  async getFleetServices(windowDays: number): Promise<FleetServicesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/fleet/services?window=${windowDays}`,
    );
    return parseResponse(response, 'fleet services');
  }

  async getFleetServiceResources(
    service: string,
    windowDays: number,
  ): Promise<ServiceResourcesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/fleet/service/${encodeURIComponent(service)}/resources?window=${windowDays}`,
    );
    return parseResponse(response, `fleet service "${service}" resources`);
  }

  async getMonthlyHistory(
    opts: { vp?: string; head?: string; team?: string; months?: number } = {},
  ): Promise<MonthlyHistoryResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (opts.vp) params.set('vp', opts.vp);
    if (opts.head) params.set('head', opts.head);
    if (opts.team) params.set('team', opts.team);
    if (opts.months) params.set('months', String(opts.months));
    const qs = params.toString();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/monthly${qs ? `?${qs}` : ''}`,
    );
    return parseResponse(response, 'monthly history');
  }

  async getMonthlyItems(month: string): Promise<MonthlyItemsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/monthly-items?month=${encodeURIComponent(month)}`,
    );
    return parseResponse(response, `monthly items for ${month}`);
  }

  async getTotalOverview(windowDays: number): Promise<TotalOverviewResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/overview?window=${windowDays}`,
    );
    return parseResponse(response, 'total overview');
  }

  async getBudgetOutlook(
    opts: { vp?: string; head?: string; team?: string; year?: number } = {},
  ): Promise<BudgetOutlookResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (opts.vp) params.set('vp', opts.vp);
    if (opts.head) params.set('head', opts.head);
    if (opts.team) params.set('team', opts.team);
    if (opts.year) params.set('year', String(opts.year));
    const qs = params.toString();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/budget${qs ? `?${qs}` : ''}`,
    );
    return parseResponse(response, 'budget outlook');
  }

  async getSaasCosts(windowDays: number): Promise<SaasCostsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/saas/costs?window=${windowDays}`,
    );
    return parseResponse(response, 'SaaS costs');
  }

  async getCallouts(): Promise<CalloutsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/callouts`);
    return parseResponse(response, 'callouts');
  }

  async getTaxonomy(): Promise<TaxonomyResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/taxonomy`);
    return parseResponse(response, 'taxonomy');
  }

  async getTeams(windowDays = 30): Promise<TeamListResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/teams?window=${windowDays}`,
    );
    return parseResponse(response, 'teams');
  }

  async getOptimizations(team?: string): Promise<OptimizationsResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = team
      ? `${baseUrl}/optimizations?team=${encodeURIComponent(team)}`
      : `${baseUrl}/optimizations`;
    const response = await this.fetchApi.fetch(url);
    return parseResponse(response, 'optimizations');
  }

  async getCostByTag(opts: {
    group: CostByTagGroup;
    period: CostByTagPeriod;
  }): Promise<CostByTagResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/breakdown/by-tag?group=${opts.group}&period=${opts.period}`,
    );
    return parseResponse(response, `cost by ${opts.group}`);
  }

  async getEc2Commitment(windowDays = 30): Promise<Ec2CommitmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ec2/commitment?window=${windowDays}`,
    );
    return parseResponse(response, 'EC2 commitment coverage');
  }
}

async function parseResponse<T>(response: Response, label: string): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(
      body.error ?? `Failed to fetch ${label}: HTTP ${response.status}`,
    );
  }
  return response.json();
}
